import React from 'react';
import { IconType } from 'react-icons';
import ChakraIcon from '../components/common/ChakraIcon';

import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Flex,
  Image,
  Stack,
  Icon,
  Button,
  Divider,
  VStack,
  HStack,
  Badge,
  Avatar,
  useBreakpointValue,
  useColorModeValue,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { 
  FaQuoteLeft, 
  FaQuoteRight, 
  FaChartLine, 
  FaUsers,
  FaGraduationCap,
  FaHome,
  FaSeedling,
  FaHeartbeat
} from 'react-icons/fa';

// Motion components
const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
const MotionText = motion(Text);
const MotionHeading = motion(Heading);

const ImpactStories: React.FC = () => {
  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.8 } }
  };
  
  const slideUp = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
  };
  
  const staggerChildren = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  // Impact metrics data
  const impactMetrics = [
    {
      number: '500+',
      label: 'Community Members Trained',
      icon: FaGraduationCap,
      color: 'brand.500'
    },
    {
      number: '10+',
      label: 'Community Projects',
      icon: FaHome,
      color: 'green.500'
    },
    {
      number: '5+',
      label: 'Partner Organizations',
      icon: FaUsers,
      color: 'earth.500'
    },
    {
      number: '3',
      label: 'Regions Served',
      icon: FaSeedling,
      color: 'brand.500'
    }
  ];

  // Testimonial data
  const testimonials = [
    {
      quote: "ALPHA's vocational training program changed my life. I now have skills to support my family and contribute to my community.",
      name: "Marie Kabila",
      role: "Program Participant",
      location: "Kasindi",
      image: "/assets/images/photo_2025-05-04_15-32-52.jpg"
    },
    {
      quote: "The peace education workshops have helped our community resolve conflicts peacefully and build stronger relationships.",
      name: "Jean Mutombo",
      role: "Community Leader",
      location: "Beni",
      image: "/assets/images/photo_2025-05-04_15-32-55.jpg"
    },
    {
      quote: "Working with ALPHA has been transformative. Their community-centered approach ensures sustainable impact.",
      name: "Sarah Mukendi",
      role: "Partner Organization",
      location: "North Kivu",
      image: "/assets/images/photo_2025-05-04_15-32-58.jpg"
    }
  ];

  // Success stories data
  const successStories = [
    {
      title: "Women's Cooperative in Kasindi",
      description: "A group of 25 women formed a cooperative with ALPHA's support, creating sustainable livelihoods through agriculture and handicrafts.",
      impact: "Increased household income by 40% for participating families, improved nutrition, and funded education for 45 children.",
      category: "Economic Empowerment",
      image: "/assets/images/photo_2025-05-04_15-33-02.jpg"
    },
    {
      title: "Youth Peace Ambassadors",
      description: "ALPHA trained 50 young people as peace ambassadors who now lead conflict resolution initiatives in their communities.",
      impact: "Reduced incidents of youth violence by 30% and established 5 peace clubs in local schools reaching over 500 students.",
      category: "Peace Building",
      image: "/assets/images/photo_2025-05-04_15-33-06.jpg"
    },
    {
      title: "Community Garden Project",
      description: "A collaborative initiative to establish community gardens providing fresh produce and agricultural training.",
      impact: "Created food security for 75 families, introduced 12 new vegetable varieties, and established a seed bank for future planting.",
      category: "Food Security",
      image: "/assets/images/photo_2025-05-04_15-33-10.jpg"
    },
    {
      title: "Trauma Healing Workshops",
      description: "ALPHA conducted trauma healing workshops for communities affected by conflict and displacement.",
      impact: "Provided psychological support to 120 individuals, trained 15 local counselors, and established 3 support groups that continue to meet regularly.",
      category: "Mental Health",
      image: "/assets/images/photo_2025-05-04_15-33-14.jpg"
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        position="relative"
        h={{ base: '40vh', md: '50vh' }}
        bgImage="linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('/assets/images/photo_2025-05-04_15-33-17.jpg')"
        bgPosition="center"
        bgSize="cover"
        color="white"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Container maxW="container.xl" textAlign="center">
          <MotionHeading
            as={motion.h1}
            fontSize={{ base: '3xl', md: '5xl' }}
            fontWeight="bold"
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            Our Impact
          </MotionHeading>
          
          <MotionText
            fontSize={{ base: 'lg', md: 'xl' }}
            mt={4}
            maxW="800px"
            mx="auto"
            initial="hidden"
            animate="visible"
            variants={slideUp}
          >
            Stories of transformation and positive change in the communities we serve.
          </MotionText>
        </Container>
      </Box>

      {/* Impact Metrics */}
      <Box py={{ base: 12, md: 20 }} bg="brand.500" color="white">
        <Container maxW="container.xl">
          <MotionBox
            textAlign="center"
            mb={16}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <Heading
              as="h2"
              fontSize={{ base: '2xl', md: '3xl' }}
              mb={4}
            >
              Our Impact in Numbers
            </Heading>
            
            <Divider w="100px" borderColor="white" borderWidth="2px" mx="auto" mb={6} />
            
            <Text
              fontSize={{ base: 'md', md: 'lg' }}
              maxW="800px"
              mx="auto"
            >
              We measure our success through improved access to resources, reduced violence, increased well-being, and strengthened community resilience.
            </Text>
          </MotionBox>
          
          <MotionFlex
            as={motion.div}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
            direction={{ base: 'column', md: 'row' }}
            justify="space-between"
            align="center"
            textAlign="center"
          >
            {impactMetrics.map((metric, index) => (
              <MotionBox key={index} variants={slideUp} p={6}>
                <Flex
                  w="70px"
                  h="70px"
                  borderRadius="full"
                  bg="whiteAlpha.200"
                  color="white"
                  justify="center"
                  align="center"
                  mb={2}
                >
                  <ChakraIcon icon={metric.icon} boxSize="32px" />
                </Flex>
                <Heading as="h3" fontSize={{ base: '4xl', md: '6xl' }}>
                  {metric.number}
                </Heading>
                <Text fontSize="lg" fontWeight="medium">
                  {metric.label}
                </Text>
              </MotionBox>
            ))}
          </MotionFlex>
        </Container>
      </Box>

      {/* Success Stories */}
      <Box py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <MotionBox
            textAlign="center"
            mb={16}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <Heading
              as="h2"
              fontSize={{ base: '2xl', md: '3xl' }}
              mb={4}
            >
              Success Stories
            </Heading>
            
            <Divider w="100px" borderColor="brand.500" borderWidth="2px" mx="auto" mb={6} />
            
            <Text
              fontSize={{ base: 'md', md: 'lg' }}
              maxW="800px"
              mx="auto"
            >
              These are just a few examples of the positive change we've helped create in communities across the Democratic Republic of Congo.
            </Text>
          </MotionBox>
          
          <MotionFlex
            as={motion.div}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={staggerChildren}
            direction="column"
            gap={16}
          >
            {successStories.map((story, index) => (
              <MotionBox
                key={index}
                mb={16}
                variants={slideUp}
              >
                <MotionFlex
                  direction={{ base: 'column', lg: index % 2 === 0 ? 'row' : 'row-reverse' }}
                  align="center"
                  justify="space-between"
                >
                  <Box
                    flex="1"
                    mr={{ base: 0, lg: index % 2 === 0 ? 10 : 0 }}
                    ml={{ base: 0, lg: index % 2 === 0 ? 0 : 10 }}
                    mb={{ base: 10, lg: 0 }}
                  >
                    <Image
                      src={story.image}
                      alt={story.title}
                      borderRadius="lg"
                      shadow="xl"
                      w="full"
                      h={{ base: '250px', md: '350px' }}
                      objectFit="cover"
                    />
                  </Box>
                  
                  <Box flex="1">
                    <Badge
                      colorScheme={
                        story.category === "Economic Empowerment" ? "blue" :
                        story.category === "Peace Building" ? "green" :
                        story.category === "Food Security" ? "orange" :
                        "purple"
                      }
                      mb={4}
                    >
                      {story.category}
                    </Badge>
                    
                    <Heading
                      as="h3"
                      fontSize={{ base: 'xl', md: '2xl' }}
                      mb={4}
                    >
                      {story.title}
                    </Heading>
                    
                    <Text fontSize={{ base: 'md', md: 'lg' }} mb={6}>
                      {story.description}
                    </Text>
                    
                    <Box
                      p={4}
                      bg="gray.50"
                      borderRadius="md"
                      borderLeft="4px solid"
                      borderColor="brand.500"
                    >
                      <Heading as="h4" fontSize="md" mb={2}>
                        Impact:
                      </Heading>
                      <Text>{story.impact}</Text>
                    </Box>
                  </Box>
                </MotionFlex>
              </MotionBox>
            ))}
          </MotionFlex>
        </Container>
      </Box>

      {/* Testimonials */}
      <Box py={{ base: 12, md: 20 }} bg="gray.50">
        <Container maxW="container.xl">
          <MotionBox
            textAlign="center"
            mb={16}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <Heading
              as="h2"
              fontSize={{ base: '2xl', md: '3xl' }}
              mb={4}
            >
              Testimonials
            </Heading>
            
            <Divider w="100px" borderColor="brand.500" borderWidth="2px" mx="auto" mb={6} />
            
            <Text
              fontSize={{ base: 'md', md: 'lg' }}
              maxW="800px"
              mx="auto"
            >
              Hear directly from the people whose lives have been touched by our work.
            </Text>
          </MotionBox>
          
          <MotionFlex
            as={motion.div}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
          >
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={10} w="full">
              {testimonials.map((testimonial, index) => (
                <MotionBox
                  key={index}
                  p={8}
                  borderRadius="lg"
                  boxShadow="md"
                  bg="white"
                  position="relative"
                  sx={{ transition: 'all 0.3s ease' }}
                  _hover={{ transform: 'translateY(-5px)', boxShadow: 'lg' }}
                  variants={slideUp}
                >
                  <Box
                    position="absolute"
                    top={4}
                    left={4}
                    opacity={0.4}
                  >
                    <ChakraIcon icon={FaQuoteLeft} boxSize="32px" color="#4A90E2" />
                  </Box>
                  
                  <Text fontSize="md" mb={8} pt={6} px={4}>
                    "{testimonial.quote}"
                  </Text>
                  
                  <Flex align="center">
                    <Avatar
                      src={testimonial.image}
                      name={testimonial.name}
                      size="md"
                      mr={4}
                    />
                    <Box>
                      <Text fontWeight="bold">{testimonial.name}</Text>
                      <Text fontSize="sm" color="gray.600">
                        {testimonial.role}, {testimonial.location}
                      </Text>
                    </Box>
                  </Flex>
                </MotionBox>
              ))}
            </SimpleGrid>
          </MotionFlex>
        </Container>
      </Box>

      {/* Annual Report */}
      <Box py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <MotionFlex
            direction={{ base: 'column', md: 'row' }}
            align="center"
            justify="space-between"
            bg="brand.50"
            p={{ base: 8, md: 12 }}
            borderRadius="lg"
            boxShadow="md"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
          >
            <MotionBox
              flex="1"
              mr={{ base: 0, md: 8 }}
              mb={{ base: 6, md: 0 }}
              variants={slideUp}
            >
              <Heading
                as="h2"
                fontSize={{ base: '2xl', md: '3xl' }}
                mb={4}
                color="brand.500"
              >
                Annual Impact Report
              </Heading>
              
              <Text fontSize={{ base: 'md', md: 'lg' }} mb={4}>
                For a comprehensive overview of our impact, download our latest annual report. Learn about our programs, partnerships, and the difference we're making in communities across the Democratic Republic of Congo.
              </Text>
            </MotionBox>
            
            <MotionBox variants={slideUp}>
              <Button
                variant="primary"
                size="lg"
                rightIcon={<ChakraIcon icon={FaChartLine} boxSize="20px" />}
              >
                Download Report
              </Button>
            </MotionBox>
          </MotionFlex>
        </Container>
      </Box>

      {/* Call to Action */}
      <Box py={{ base: 12, md: 20 }} bg="gray.50">
        <Container maxW="container.xl">
          <MotionFlex
            direction={{ base: 'column', md: 'row' }}
            align="center"
            justify="space-between"
            bg="white"
            p={{ base: 8, md: 12 }}
            borderRadius="lg"
            boxShadow="md"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
          >
            <MotionBox
              flex="1"
              mr={{ base: 0, md: 8 }}
              mb={{ base: 6, md: 0 }}
              variants={slideUp}
            >
              <Heading
                as="h2"
                fontSize={{ base: '2xl', md: '3xl' }}
                mb={4}
              >
                Be Part of Our Impact Story
              </Heading>
              
              <Text fontSize={{ base: 'md', md: 'lg' }} mb={4}>
                Join us in creating positive change in communities across the Democratic Republic of Congo. Your support can help us expand our reach and deepen our impact.
              </Text>
            </MotionBox>
            
            <MotionBox variants={slideUp}>
              <Stack
                direction={{ base: 'column', sm: 'row' }}
                spacing={4}
              >
                <Button
                  variant="primary"
                  size="lg"
                >
                  Donate Now
                </Button>
                <Button
                  variant="outline"
                  colorScheme="blue"
                  size="lg"
                >
                  Get Involved
                </Button>
              </Stack>
            </MotionBox>
          </MotionFlex>
        </Container>
      </Box>
    </Box>
  );
};

export default ImpactStories;
